import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useRef } from 'react';

import { Button } from '$partials/ui';
import { Dispatcher as IconDispatcher } from '$partials/icon';
import { IconUniPlan } from '$partials/icon/icons';
import { LocaleSelector } from '$partials/global';
import { useLanguage } from '$contexts/language.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Navigation = forwardRef(({
    className     = null,
    facebook      = null,
    instagram     = null,
    isOpen        = null,
    items         = [],
    linkedin      = null,
    localePages   = null,
    toggleDrawer  = () => {},
    twitter       = null,
    wechat        = null,
    weibo         = null,
    youtube       = null
}, navigationButtonRef) => {
    // references
    const navigationRef = useRef();

    // hooks
    const {
        current: localeCurrent,
        translate: t
    } = useLanguage();

    useEffect(() => {
        if (navigationRef.current) {
            if (isOpen) {
                disableBodyScroll(navigationRef.current);
            } else {
                enableBodyScroll(navigationRef.current);
            }
        }
    }, [ isOpen ]);

    // render
    return (
        <div
            className={`navigation${isOpen ? ' navigation--is-open ' : ' '}${className}`}
            ref={navigationRef}>
            {/* button */}
            <Button
                action={e => toggleDrawer(e, 'navigation')}
                aria-label={t('Open navigation')}
                ref={navigationButtonRef}>
                <span className='accessible-hide'>
                    {t('Open navigation')}
                </span>

                <span>
                    <IconDispatcher name='IconBurger' />
                </span>
            </Button>

            <div className='navigation__drawer side-bar black'>
                <div className='side-bar__aside bg-lemon-yellow-crayola'>
                    <>
                        <div className='navigation'>
                            <Button
                                action={e => toggleDrawer(e, 'navigation')}
                                aria-label={t('Close navigation')}>
                                <span className='accessible-hide'>
                                    {t('Close navigation')}
                                </span>

                                <IconDispatcher name='IconCross' />
                            </Button>
                        </div>

                        <div className='search'>
                            <Button
                                action={e => toggleDrawer(e, 'search')}
                                aria-label={t('Open search')}>
                                <span className='accessible-hide'>
                                    {t('Open search')}
                                </span>

                                <IconDispatcher name='IconSearch' />
                            </Button>
                        </div>

                        <a
                            className='block line-height-0 black'
                            href={`/${localeCurrent === 'en' ? '' : localeCurrent}`}>
                            <IconUniPlan />
                        </a>

                        <div className='height-100 flex items-end'>
                            <LocaleSelector pages={localePages} />
                        </div>
                    </>
                </div>

                <div className='side-bar__content bg-lemon-yellow-crayola md-gradient-bg-lemon-yellow-crayola'>
                    <div className='height-100 overflow-x-hidden overflow-y-auto'>
                        <div className='container'>
                            <div className='mln8 sm-mln0 md-ml0'>
                                <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                                    <div className='flex flex-wrap'>
                                        {/* side title */}
                                        <div className='absolute top-0 md-relative md-col-2 hide md-show sm-pt10 md-pt13'>
                                            <div className='px-gutter'>
                                                <p className='type-style-5 side-title side-title--less-top'>
                                                    {t('Menu')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* content / links */}
                                        <div className='col-12 sm-col-6 md-col-5 sm-push-left-6 md-push-left-0 pt10 sm-pt9 md-pt13'>
                                            <div className='ml9 sm-ml9 md-ml0'>
                                                <div className='px-gutter'>
                                                    <nav>
                                                        <ul className='list-reset'>
                                                            {items.map((item, idx) => {
                                                                return (
                                                                    <li key={idx}>
                                                                        <a
                                                                            className='link-reset block type-style-2 hover-hans-purple pb2 sm-pb2 md-pb0'
                                                                            href={item.page?.url}>
                                                                            {item.page?.title}
                                                                        </a>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='mln8 sm-mln0 md-ml0'>
                                <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                                    <div className='flex flex-wrap'>
                                        {/* side title */}
                                        <div className='absolute top-0 md-relative md-col-2 hide md-show sm-pt9 md-pt14'>
                                            <div className='px-gutter'>
                                                <p className='type-style-5 side-title side-title--lesser-top'>
                                                    {t('Stay in Touch')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* content / links */}
                                        <div className='col-12 sm-col-6 md-col-5 sm-push-left-6 md-push-left-0 py10 sm-pt10 md-pt14'>
                                            <div className='ml9 sm-ml9 md-ml0'>
                                                <div className='px-gutter'>
                                                    <ul className='list-reset'>
                                                        <li className='border-bottom border-black'>
                                                            <p className='type-style-3 mb4 mt0 capitalise'>
                                                                {t('Follow Us')}:
                                                            </p>
                                                        </li>

                                                        {facebook && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={facebook}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    Facebook
                                                                </a>
                                                            </li>
                                                        )}

                                                        {instagram && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={instagram}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    Instagram
                                                                </a>
                                                            </li>
                                                        )}

                                                        {linkedin && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={linkedin}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    LinkedIn
                                                                </a>
                                                            </li>
                                                        )}

                                                        {twitter && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={twitter}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    Twitter
                                                                </a>
                                                            </li>
                                                        )}

                                                        {wechat && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={wechat}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    WeChat
                                                                </a>
                                                            </li>
                                                        )}

                                                        {weibo && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={weibo}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    Weibo
                                                                </a>
                                                            </li>
                                                        )}

                                                        {youtube && (
                                                            <li className='border-bottom border-black'>
                                                                <a
                                                                    className='link-reset block mb3 mt4 type-style-6 hans-purple'
                                                                    href={youtube}
                                                                    rel='noreferrer noopener'
                                                                    target='_blank'>
                                                                    YouTube
                                                                </a>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

Navigation.displayName = 'Navigation';

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    className    : PropTypes.string,
    facebook     : PropTypes.string,
    instagram    : PropTypes.string,
    isOpen       : PropTypes.bool.isRequired,
    items        : PropTypes.array,
    linkedin     : PropTypes.string,
    localePages  : PropTypes.array,
    toggleDrawer : PropTypes.func.isRequired,
    twitter      : PropTypes.string,
    wechat       : PropTypes.string,
    weibo        : PropTypes.string,
    youtube      : PropTypes.string
};

Navigation.propTypes = propTypes;

export { Navigation };

export default Navigation;
